<template>
  <nav>
    <v-row
      v-if="!loadingData"
      class="fill-height"
      align="center"
    >
      <v-col
        cols="12"
        sm="8"
        md="6"
        offset-sm="2"
        offset-md="3"
        align="center"
      >
        <AlertMessages
          v-if="alertValue"
          :alert-type="alertType" 
          :alert-msg="alertMsg"
          @reset="alertValue = false"
        />
        <v-card
          max-width="800"
          elevation="0"
          rounded="xxl"
          color="background"
        > 
          <v-card-title class="text-h5 font-weight-bold text-center justify-center mb-4 no-break">
            {{ $t('shareHubTitle') }}
          </v-card-title>
          <v-card-subtitle class="text-body-1 darktext--text d-none d-sm-block no-break">
            {{ $t('shareHubDescription') }}
          </v-card-subtitle>
          <v-form
            ref="form"
            v-model="valid"
            @submit.prevent
          >
            <v-card-subtitle class="text-body-2 greytext--text ">
              {{ $t('shareHubUsersFieldDescription') }}
            </v-card-subtitle>
            <v-autocomplete
              v-model="selectedUsers"
              :menu-props="{
                rounded: 'xxl'
              }"
              :items="items"
              :loading="isLoading"
              :search-input.sync="search"
              color="white"
              hide-selected
              item-text="searchTerm"
              item-value="id"
              :placeholder="$t('typeToSearch')"
              return-object
              multiple
              chips
              clearable
              solo
              class="rounded-xxl text-body-2 px-1"
              @blur="closeSearch"
              autofocus             
              data-testid="users-input"
              @input="checkUserExist"
              @change="change"
            >
              <template v-slot:no-data>
                <p class="mx-5 mt-4">
                  {{ $t('noDataText') }}
                </p>
              </template>
              <template v-slot:selection="data">
                <v-chip
                  v-if="data.item.type == 'users'"
                  v-bind="data.attrs"
                  close
                  :color="getColor(data.item.attributes.email)"
                  data-testid="user-chip"
                  @click:close="remove(data.item)"
                >
                  <v-avatar left>
                    <v-img
                      v-if="data.item.attributes.avatar_thumb == null"
                      src="../../assets/user_images/profile-avatar.png"
                    />
                    <v-img
                      v-else
                      :src="data.item.attributes.avatar_thumb"
                    />
                  </v-avatar>
                  {{ data.item.attributes.first_name }} {{ data.item.attributes.last_name }}
                </v-chip>
                <v-chip
                  v-else
                  v-bind="data.attrs"
                  close
                  :color="getColor(data.item.attributes.email)"
                  data-testid="user-chip"
                  @click:close="remove(data.item)"
                >
                  <v-avatar left>
                    <v-img src="../../assets/user_images/target-group-avatar.png" />
                  </v-avatar>
                  {{ data.item.attributes.name }}
                </v-chip>
              </template>
              <template v-slot:item="data">
                <v-list-item-avatar
                  v-if="data.item.type == 'users'"
                  rounded="lg"
                >
                  <img
                    v-if="data.item.attributes.avatar_thumb == null"
                    src="../../assets/user_images/profile-avatar.png"
                  >
                  <img
                    v-else
                    :src="data.item.attributes.avatar_thumb"
                  >
                </v-list-item-avatar>
                <v-list-item-avatar
                  v-else
                  rounded="lg"
                >
                  <img
                    v-if="data.item.attributes.avatar_thumb == null"
                    src="../../assets/user_images/target-group-avatar.png"
                  >
                </v-list-item-avatar>
                <v-list-item-content v-if="data.item.type == 'users'">
                  <v-list-item-title>{{ data.item.attributes.first_name + ' ' + data.item.attributes.last_name }} </v-list-item-title>
                  <v-list-item-subtitle> {{ data.item.attributes.email }} </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-content v-else>
                  <v-list-item-title>{{ data.item.attributes.name }} </v-list-item-title>
                </v-list-item-content>
              </template>
            </v-autocomplete>
            <div v-if="hubType != 'Restricted'">
              <v-card-subtitle class="text-body-2 greytext--text ">
                {{ $t('shareHubExternalUsersFieldDescription') }}
              </v-card-subtitle>
              <v-combobox
                v-model="selectedExternalUsers" 
                data-testid="user-email-input"
                :placeholder="$t('addEmailsPlaceholder')"
                chips
                clearable
                solo
                class="rounded-xxl text-body-2"
                :delimiters="[',', ' ']"
                :rules="emailRules"
                :error-messages="errors"
                multiple
                @input="checkUserExistInSpace"
              >
                <template v-slot:selection="{ attrs, item, }">
                  <v-chip
                    v-bind="attrs" 
                    close
                    :color="getColorExternalUsers(item)"
                    @click:close="removeEmail(item)"
                  >
                    <strong>{{ item }}</strong>&nbsp;
                  </v-chip>
                </template>
              </v-combobox>
            </div>
            <v-card-subtitle class="text-caption greytext--text py-0 mb-8">
              {{ $t('shareHubUsersFieldNote') }}
            </v-card-subtitle>
            <v-textarea
              v-model="message"
              data-testid="message-textarea"
              rounded
              auto-grow
              solo
              name="textarea"
              :placeholder="$t('shareHubMessageField')"
            />                   
          </v-form>
        </v-card>
        <v-row>
          <v-col align="center">
            <FormFooter
              next-button-text="share"
              :loading="loading"
              :cancel-button-link="cancelLink"
              @proceedSubmit="shareHub"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>             
  </nav>
</template>
<script>
import i18n from '../../plugins/i18n'
import { searchSpaceUsers } from '../../api/spaceApi'
import { searchTargetGroups } from '../../api/targetGroupApi'
import { checkUserInHub, checkUserInSpace, shareHub, getHub } from '../../api/hubApi'
import FormFooter  from '../../components/FormFooter'
import AlertMessages  from '../../components/AlertMessages'

export default {
    components: {
        FormFooter,
        AlertMessages
    },
    data () {
        return {
            spaceId: window.$cookies.get('space_id'),
            descriptionLimit: 60,
            selectedUsers: [],
            selectedExternalUsers: [],
            search: null,
            isLoading: false,
            entries:[],
            alertMsg:'',
            alertValue:false,
            loading: false,
            valid: true,
            showRedError: false,
            color: '',
            existingUsersInHub: [],
            existingInSpace: [],
            guestInHub: [],
            message: '',
            hubType:'',
            hubId: this.$route.params.id,
            //items: [],
            autocompleteRules: [
                v => {
                    if (!v || v.length < 1){
                        return  i18n.t('labelInputRequired');
                    }
                    else return true;
                }
            ],
            emailRules: [
                v => {
                    if (!v || v.length < 1){
                        return true;
                    }else if (v.length > 0) {
                        for (let i = 0; i < v.length; i++) {
                            if(!(/.+@.+/.test(v[i]))){
                                return i18n.t('emailErrorValid');
                            }
                        }
                        if (this.showRedError) {
                            return i18n.t('showErrorWhenUserIsMember');
                        } else 
                            return true;
                    } 
                    else return true;
                }
            ],
            errors: [],
            loadingData:false,
            cancelLink:'/hub/'+this.$route.params.id,
            previousRoute: null,
            role: localStorage.space ? JSON.parse(localStorage.space).role : '',
        }
    },
    computed: {
        items () {
            return this.entries.map(entry => {
                if (entry.type == 'users') {
                    const searchTerm = entry.attributes.first_name + ' ' + entry.attributes.last_name + ' ' + entry.attributes.email
                    return Object.assign({}, entry, { searchTerm })
                } else if (entry.type == 'target-groups') {
                    const searchTerm = entry.attributes.name
                    return Object.assign({}, entry, { searchTerm })
                }
            })
        },
        selectedUsersEmails() {
            return this.selectedUsers.map(entry => {
                if (entry.type == 'users') {
                    if(entry.attributes.email){
                        return entry.attributes.email
                    }
                }
            })
        },
        selectedTargetGroups() {
            return this.selectedUsers.map(entry => {
                if (entry.type == 'target-groups') {
                    if(entry.id){
                        return entry.id
                    }
                }
            })
        }
    },
    watch: {
        async search (val) {
            if (this.isLoading) return 

            var entriesUsers = []
            var entriesTargetGroups = []
            if(val && val.trim().length >= 2){
                this.isLoading = true
                try {
                    const response = await searchSpaceUsers(this.spaceId, val)
                    if(response.status == 200) {
                        // this.entries = response.data.data
                        entriesUsers = response.data.data
                        entriesUsers.forEach(function(item) {
                            item.sort_term = item.attributes.first_name.toLowerCase() + ' ' + item.attributes.last_name.toLowerCase() + ' ' + item.attributes.email.toLowerCase()
                        })
                    }            
                } catch (e) {
                    if(e.response){
                        this.alertValue = true
                        if (e.response.status == 403){
                            this.$router.push({name: 'PageAccessDenied'})
                        } else {
                            this.alertType = 'error'
                            this.alertMsg =  i18n.t('serverError')
                        }
                    }            
                }
                try {
                    const response = await searchTargetGroups(this.spaceId, val)
                    if(response.status == 200) {
                        // this.entries = response.data.data
                        entriesTargetGroups = response.data.data
                        entriesTargetGroups.forEach(function(item) {
                            item.sort_term = item.attributes.name.toLowerCase() + ' ' + item.attributes.created_at
                        })
                    }            
                } catch (e) {
                    if(e.response){
                        this.alertValue = true
                        if(e.response.status == 403){
                            this.$router.push({name: 'PageAccessDenied'})
                        } else {
                            this.alertType = 'error'
                            this.alertMsg =  i18n.t('serverError')
                        }
                    }            
                }

                // merge the arrays with users and target groups
                Array.prototype.push.apply(entriesUsers, entriesTargetGroups)
                // Sort users and target groups together
                entriesUsers.sort(function(a,b) {return (a.sort_term > b.sort_term) ? 1 : ((b.sort_term > a.sort_term) ? -1 : 0)} )
                this.entries = entriesUsers
                this.isLoading = false
            }    
            /*if(val == null || val == ''){
                this.entries= []
                this.$nextTick(() => {
                    this.entries= []
            })
            }*/  
            this.isLoading = false
        }
    },
    async created(){ 
        this.loadingData = true 
        if(this.role && this.role != "NA" ){
        await this.getHub(); 
        } 
        this.loadingData = false 
    },
    beforeRouteEnter(to, from, next) {
        if(from.path != null && from.path != '/redirect' && from.path != '/'){
            next(vm => {
                vm.cancelLink = from.path,
                vm.previousRoute = from
            });
        }else { 
            next();
        }
    },
    methods: {
        change(){
            this.search = ''
        },
        remove(item) {
            this.selectedUsers.splice(this.selectedUsers.indexOf(item), 1)
            this.selectedUsers = [...this.selectedUsers]
        },
        closeSearch(){
          this.search = ''
          this.entries = this.selectedUsers.slice();
        },
        removeEmail(item) {
            this.loading = false
            this.showRedError = false
            this.errors = []
            this.selectedExternalUsers.splice(this.selectedExternalUsers.indexOf(item), 1)
            this.selectedExternalUsers = [...this.selectedExternalUsers]
        },
        async getHub(){
            try {
                const response = await getHub(this.hubId)
                if(response.status == 200) {
                    
                    this.hubType = response.data.data.attributes.sharing_setting
                }  
            } catch (e) {
                if(e.response){
                    this.alertType = 'error'
                    this.alertMsg =  i18n.t('serverError')
                } 
            }
        },
        async shareHub(){
            this.$refs.form.validate()
            if(this.valid && (this.selectedExternalUsers.length || this.selectedUsers.length) ){
                this.loading = true
                try {
                    let data = {
                        data: {
                            type: "hub-users", 
                            attributes: {
                                user_email: this.selectedUsersEmails.filter(n => n),
                                target_group_id: this.selectedTargetGroups.filter(n => n),
                                external_emails: this.selectedExternalUsers.filter(n => n),
                                message: this.message
                            }
                        }
                    };
                    const response = await shareHub(this.$route.params.id, data)
                    if(response.status == 200) {
                        if(this.previousRoute){
                            if(this.previousRoute.name !== null){
                                var params = this.previousRoute.params
                                params.successfullySharedHub = true
                                this.$router.push({name: this.previousRoute.name, params: params})
                            }else {
                                this.$router.push({name: 'ViewHub', params: {id: this.$route.params.id, successfullySharedHub: true}})
                            }
                        }else{
                            this.$router.push({name: 'ViewHub', params: {id: this.$route.params.id, successfullySharedHub: true}})
                        }  
                    }            
                } catch (e) {
                    if(e.response){
                        this.alertValue = true
                        if (e.response.status == 403){
                            this.$router.push({name: 'PageAccessDenied'})
                        } else if(e.response.status == 404) {
                            this.$router.push({name: 'PageNotFound'})
                        } else {
                            this.alertType = 'error'
                            this.alertMsg =  i18n.t('serverError')
                        }
                    }            
                }
                this.loading =false
            }
            
        },
        getColor (item) {
            if(this.existingUsersInHub.indexOf(item) >= 0){
                return "warning"
            }else {
                return "accent"

            }
        },
        getColorExternalUsers (item) {
            if(this.existingInSpace.indexOf(item) >= 0){
                this.loading = true
                this.showRedError = true
                return "error"
            }else if(this.guestInHub.indexOf(item) >= 0){
                return "warning"
            }else {

                if(!(/.+@.+/.test(item))){
                    return "error"
                }

                return "accent"
            }
        },
        async checkUserExist(){
            const selectedUsersEmails = this.selectedUsersEmails;
            const lastEmail = selectedUsersEmails.slice().pop();  
            if (lastEmail) {
                try {
                    let data = {
                        data: {
                            type: "hub-users",
                            attributes: {
                                user_email: lastEmail
                            }
                        }
                    };
                    const response = await checkUserInHub(this.$route.params.id, data)
                    if(response.status == 200){
                        return true
                    }
                }catch (e) {
                    if(e.response){
                        if(e.response.status == 400){
                            this.existingUsersInHub.push(lastEmail)
                        } else {
                            return true
                        }
                    }
                }
            }
        },
        async checkUserExistInSpace(){
            const selectedExternalUsers = this.selectedExternalUsers;
            // console.table('external users list' + JSON.stringify(selectedExternalUsers, null, 4))
            const lastEmail = selectedExternalUsers.slice().pop()
            if (lastEmail) {
                this.loading = false
                this.showRedError = false
                this.errors = []
                try {
                    let data = {
                        data: {
                            type: "hub-users",
                            attributes: {
                                user_email: lastEmail
                            }
                        }
                    };
                    const response = await checkUserInSpace(this.$route.params.id, data)
                    if(response.status == 200){
                        this.errors = []
                        return true
                    }
                }catch (e) {
                    if(e.response){
                        if(e.response.status == 400){
                            if(e.response.data){
                                for(let i = 0; i < e.response.data.errors.length; i++){
                                    if (e.response.data.errors[i].title == "Hub Member") {
                                        this.showRedError = true
                                        this.errors = [i18n.t('showErrorWhenUserIsMember')]
                                        this.existingInSpace.push(lastEmail)
                                        
                                    } else if(e.response.data.errors[i].title == "Space Member") {
                                        this.showRedError = true
                                        this.errors = [i18n.t('showErrorWhenUserIsMember')]
                                        this.existingInSpace.push(lastEmail)
                                        
                                    } else {
                                        this.guestInHub.push(lastEmail)
                                    }
                                }
                            }
                        } else {
                            return true
                        }
                    }
                }
            }
        }
    }
}

</script>